<script setup lang="ts">
/**
 * The table style library: every way to draw a table the firm has saved, and
 * the editor to make or change one.
 *
 * A list down the side (name, whether it is the default, how many tables
 * follow it), the selected style's settings beside it, and a sample table
 * drawn in it as each setting is changed, so a style is judged on a table and
 * not on a list of options.
 *
 * Nothing reaches a table until "Save": tables follow a style by id, so saving
 * changes every one of them at once, and that should be a decision. Only the
 * person who saved a style can change or delete it; anyone can duplicate one
 * to start their own. A style some table still follows cannot be deleted.
 */
import { useI18n } from 'vue-i18n'

import { computed, onMounted, ref, watch } from 'vue'

import { Input } from '@/components/ui'
import ModalDialog from '@/components/ui/ModalDialog.vue'
import { layoutStyle } from '@/domain/layout'
import type { TableSection } from '@/domain/section'
import { emptyCell } from '@/domain/section'
import type { TableLook } from '@/sections/table/look'
import TableBlock from '@/sections/table/TableBlock.vue'
import TableLookControls from '@/sections/table/TableLookControls.vue'
import { NO_STYLE, type TableStyle } from '@/sections/table/tableStyle'
import { useReportStore } from '@/stores/report'
import { useTableStylesStore } from '@/stores/tableStyles'

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const library = useTableStylesStore()
const report = useReportStore()

const selectedId = ref<number | null>(null)
const name = ref('')
const look = ref<TableLook>({})
const confirmingDelete = ref(false)

const selected = computed(() => library.styles.find((style) => style.id === selectedId.value))
const mine = computed(() => (selected.value ? library.isMine(selected.value) : false))

/** What the controls and the sample draw: the saved look, until it is changed here. */
function select(style: TableStyle | undefined): void {
  selectedId.value = style?.id ?? null
  name.value = style?.name ?? ''
  look.value = { ...style?.look }
  confirmingDelete.value = false
}

onMounted(async () => {
  await library.load()
  select(library.defaultStyle ?? library.styles[0])
})

// A save changes the list under the selection: keep what is being edited, refresh what is not.
watch(
  () => library.styles,
  () => {
    if (selectedId.value !== null && !selected.value) select(library.styles[0])
  },
)

const dirty = computed(() => {
  const style = selected.value
  if (!style) return false
  return (
    name.value.trim() !== style.name || JSON.stringify(look.value) !== JSON.stringify(style.look)
  )
})

function set(key: keyof TableLook, value: TableLook[keyof TableLook] | undefined): void {
  const next: Record<string, unknown> = { ...look.value }
  if (value === undefined) delete next[key]
  else next[key] = value
  look.value = next
}

async function create(): Promise<void> {
  const created = await library.create(t('tableStyles.newName'), {})
  if (created) select(library.styles.find((style) => style.id === created.id))
}

async function duplicate(): Promise<void> {
  const style = selected.value
  if (!style) return
  const created = await library.create(t('tableStyles.copyName', { name: style.name }), look.value)
  if (created) select(library.styles.find((one) => one.id === created.id))
}

async function save(): Promise<void> {
  const style = selected.value
  const trimmed = name.value.trim()
  if (!style || !trimmed) return
  const saved = await library.update(style.id, { name: trimmed, look: look.value })
  if (saved) select(library.styles.find((one) => one.id === saved.id))
}

async function makeDefault(): Promise<void> {
  const style = selected.value
  if (style) await library.update(style.id, { isDefault: !style.isDefault })
}

async function remove(): Promise<void> {
  const style = selected.value
  if (!style) return
  if (!confirmingDelete.value) {
    confirmingDelete.value = true
    return
  }
  const deleted = await library.remove(style.id)
  confirmingDelete.value = false
  if (deleted) select(library.styles[0])
}

/** A small statement to judge a style on: a head, plain rows, a negative, two totals. */
const SAMPLE: TableSection = {
  id: 'sample',
  kind: 'table',
  title: '',
  header: [
    { value: 'En millions d’euros' },
    { value: 'Notes', align: 'center' },
    { value: '31/12/2025', align: 'center' },
    { value: '31/12/2024', align: 'center' },
  ],
  rows: [
    [
      { value: 'Chiffre d’affaires', strong: true },
      { value: '5', numeric: true, strong: true },
      { value: '7 934', numeric: true, strong: true },
      { value: '7 140', numeric: true, strong: true },
    ],
    [
      { value: 'Autres produits' },
      { value: '5', numeric: true },
      { value: '890', numeric: true },
      { value: '1 214', numeric: true },
    ],
    [
      { value: 'Achats consommés' },
      emptyCell(),
      { value: '-1 467', numeric: true },
      { value: '-1 792', numeric: true },
    ],
    [
      { value: 'Charges de personnel' },
      { value: '7', numeric: true },
      { value: '-4 529', numeric: true },
      { value: '-4 187', numeric: true },
    ],
    [
      { value: 'EBITDA', strong: true },
      emptyCell(),
      { value: '1 034', numeric: true, strong: true },
      { value: '760', numeric: true, strong: true },
    ],
    [
      { value: 'Résultat net' },
      emptyCell(),
      { value: '223', numeric: true },
      { value: '209', numeric: true },
    ],
  ],
  caption: '',
  ruled: false,
  fit: 'normal',
  columnWidths: [46, 12, 21, 21],
}

/** The sample, drawn in the look being edited and nothing else: it follows no style. */
const sample = computed<TableSection>(() => ({ ...SAMPLE, styleId: NO_STYLE, look: look.value }))
const pageStyle = computed(() => layoutStyle(report.layout))
</script>

<template>
  <ModalDialog open :title="t('tableStyles.title')" size="xlarge" @close="emit('close')">
    <div class="lib">
      <aside class="lib__list">
        <button type="button" class="b-btn lib__new" :disabled="library.busy" @click="create">
          {{ t('tableStyles.new') }}
        </button>

        <p v-if="library.loading" class="lib__note">{{ t('common.loading') }}</p>
        <p v-else-if="library.styles.length === 0" class="lib__note">
          {{ t('tableStyles.empty') }}
        </p>

        <button
          v-for="style in library.styles"
          :key="style.id"
          type="button"
          class="card"
          :class="{ 'card--on': style.id === selectedId }"
          @click="select(style)"
        >
          <span class="card__name">{{ style.name }}</span>
          <span class="card__meta">
            <span v-if="style.isDefault" class="card__default">{{ t('tableStyles.default') }}</span>
            <span>{{ t('tableStyles.usage', { count: style.usage }, style.usage) }}</span>
          </span>
        </button>
      </aside>

      <section v-if="selected" class="lib__editor">
        <label class="lib__name">
          <span class="b-label">{{ t('tableStyles.name') }}</span>
          <Input v-model="name" :disabled="!mine" maxlength="120" />
        </label>

        <p v-if="!mine" class="lib__note">
          {{ t('tableStyles.notMine', { owner: selected.owner }) }}
        </p>

        <div class="lib__work">
          <div class="lib__controls" :inert="!mine">
            <TableLookControls :look="look" @set="set" />
          </div>

          <div class="lib__sample">
            <span class="b-label">{{ t('tableStyles.sample') }}</span>
            <article class="page lib__page" :style="pageStyle">
              <TableBlock :section="sample" />
            </article>
          </div>
        </div>

        <p v-if="library.problem" class="lib__problem" role="alert">{{ library.problem }}</p>
        <p v-if="selected.usage > 0 && mine" class="lib__note">
          {{ t('tableStyles.changeNote', { count: selected.usage }, selected.usage) }}
        </p>

        <div class="lib__actions">
          <button type="button" class="b-btn" :disabled="library.busy" @click="duplicate">
            {{ t('tableStyles.duplicate') }}
          </button>
          <button
            v-if="mine"
            type="button"
            class="b-btn"
            :disabled="library.busy"
            @click="makeDefault"
          >
            {{ selected.isDefault ? t('tableStyles.unsetDefault') : t('tableStyles.setDefault') }}
          </button>
          <button
            v-if="mine"
            type="button"
            class="b-btn lib__delete"
            :disabled="library.busy"
            @click="remove"
          >
            {{ confirmingDelete ? t('tableStyles.confirmDelete') : t('tableStyles.delete') }}
          </button>
          <button
            v-if="mine"
            type="button"
            class="b-btn b-btn--primary lib__save"
            :disabled="library.busy || !dirty || !name.trim()"
            @click="save"
          >
            {{ t('tableStyles.save') }}
          </button>
        </div>
      </section>

      <p v-else class="lib__note lib__empty">{{ t('tableStyles.pick') }}</p>
    </div>
  </ModalDialog>
</template>

<style scoped>
.lib {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: var(--sp-8);
  min-height: 420px;
}

.lib__list {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  max-height: 70vh;
  overflow-y: auto;
}

.lib__new {
  align-self: flex-start;
  color: var(--accent);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-4);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-md, 6px);
  background: var(--panel);
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.card--on {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.card__name {
  font-weight: var(--weight-semibold);
}

.card__meta {
  display: flex;
  gap: var(--sp-4);
  color: var(--ink-faint);
  font-size: var(--text-xs);
}

.card__default {
  color: var(--accent);
  font-weight: var(--weight-semibold);
}

.lib__editor {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  min-width: 0;
}

.lib__name {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  max-width: 360px;
}

.lib__work {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: var(--sp-8);
}

.lib__controls {
  max-height: 58vh;
  overflow-y: auto;
  padding-right: var(--sp-3);
}

.lib__sample {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  min-width: 0;
}

/* A sheet only for its stylesheet: the width and height of a page are not wanted here. */
.lib__page {
  width: 100%;
  min-height: 0;
  margin: 0;
  padding: var(--sp-6);
  border: 1px solid var(--hairline);
  box-shadow: none;
  box-sizing: border-box;
}

.lib__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4);
  align-items: center;
}

.lib__save {
  margin-left: auto;
}

.lib__delete {
  color: var(--danger, #b3261e);
}

.lib__note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}

.lib__problem {
  margin: 0;
  color: var(--danger, #b3261e);
  font-size: var(--text-sm);
}

.lib__empty {
  align-self: center;
}
</style>
